// InventoryManagment.jsx
import React, { useState, useEffect, useContext } from "react";
import { ChevronDown, ChevronUp, Search, Plus, Pencil, RefreshCcw } from "lucide-react";
import "./InventoryAccordion.css";
import MyContext from "../../../MyContext";
import CreateOrderModal from "./CreateOrderModal";
import loader from "../../../images/Anlook.svg";
import Swal from "sweetalert2";
import axios from "axios";

const BASIC_AUTH = "YW5sb29rQGdtYWlsLmNvbTpBbmxvb2tAMTIzNDU=";

const Loader = () => (
  <div className="m-5 p-5 d-flex justify-content-center">
    <div className="d-flex flex-column align-items-center justify-content-center">
      <div className="p-3"><div className="loader" /></div>
      <div className="p-3"><img src={loader} alt="loading" height={40} /></div>
    </div>
  </div>
);

// ---- helpers ----
const safeParse = (v, fallback = null) => {
  try {
    if (typeof v !== "string") return v ?? fallback;
    const t = v.trim();
    if (!t) return fallback;
    return JSON.parse(t);
  } catch {
    return fallback;
  }
};
const dishLabelFrom = (item) => {
  const iconArr = safeParse(item?.icon_dish, null);
  const icon = Array.isArray(iconArr) ? iconArr[0] : (item?.icon_dish || item?.iconDish || "");
  const s = String(icon || "").toLowerCase();
  if (s.includes("veg")) return "Veg";
  if (s.includes("non")) return "Non-Veg";
  if (s.includes("egg")) return "Egg";
  const dishArr = safeParse(item?.dish, null);
  const d = Array.isArray(dishArr) ? String(dishArr[0]) : String(item?.dish || "");
  if (d === "1" || /veg/i.test(d)) return "Veg";
  if (d === "2" || /non/i.test(d)) return "Non-Veg";
  if (d === "3" || /egg/i.test(d)) return "Egg";
  return "";
};
const firstIconUrl = (item) => {
  const iconArr = safeParse(item?.icon_dish, null);
  return Array.isArray(iconArr) ? iconArr[0] : (item?.icon_dish || item?.iconDish || "");
};

const InventoryAccordion = ({ userID }) => {
  const { baseUrl } = useContext(MyContext);
  const { config } = useContext(MyContext);

  // table state
  const [expandedId, setExpandedId] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters/search
  const [q, setQ] = useState("");

  // categories (for modal)
  const [invCategories, setInvCategories] = useState([]);        // [{label, value, catId?}]
  const [invSubCategories, setInvSubCategories] = useState([]);  // [{label, value}]

  // add modal
  const [showAddModal, setShowAddModal] = useState(false);

  // edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSeed, setEditSeed] = useState(null); // maps server response -> modal form fields

  // barcode quick fetch
  const [barcodeValue, setBarcodeValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // -------- Fetch Inventory (table) ----------
  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getInventoryItems`, {
        method: "POST",
        headers: {
          Authorization: `Basic ${BASIC_AUTH}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userid: userID, orderType: "ALL" }),
      });
      const data = await res.json();
      if (data.responseCode === "00") {
        const mapped = (data.response || []).map((item) => {
          const dishLabel = dishLabelFrom(item);
          const discount = item?.discount ? String(item.discount) : "";
          const net = item?.discount_price ? String(item.discount_price) : "";
          const barcodeNumber = item.barcodeNumber || item.barcode || "";
          return {
            id: item.item_id,
            code: String(item.item_id),
            name: item.item_name,
            group: item.category_name || "Unknown",
            category: item.sub_category || item.item_category || "-",
            price: `₹${item.price || "0"}`,
            lastUpdated: item.lastUpdated || "—",
            inStock: item.inStock === "Y" || item.status === "Y" || item.inStock === true,
            image: item.photo,
            expandedImages: [item.photo].filter(Boolean),
            barcodeNumber,
            details: {
              "Item Name": item.item_name,
              "Category": item.category_name || "",
              "Sub Category": item.sub_category || "",
              "Size": item.size || "",
              "Dish": dishLabel || "—",
              "Price (MRP)": `₹${item.price || "0"}`,
              "Discount %": discount || "0",
              "Discounted Price": net ? `₹${net}` : "₹0",
              "Ratings": item.ratings ?? "0",
              "Status": (item.inStock === "Y" || item.status === "Y") ? "Active (Y)" : "Inactive (N)",
              "Stock": item.stock ?? "0",
              "Home Delivery": item.home_delivery === "Y" ? "Yes" : "No",
              "Takeaway": item.takeaway === "Y" ? "Yes" : "No",
              "Dining": item.dining === "Y" ? "Yes" : "No",
              "Item Type": item.item_category || "",
              "Barcode / ID": item.item_id,
              ...(barcodeNumber ? { "Barcode": barcodeNumber } : {}),
            },
            _raw: item,
          };
        });
        setInventoryData(mapped);
      } else {
        Swal.fire("Error", data.responseMessage || "Failed to load inventory", "error");
      }
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Failed to load inventory", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInventory(); /* eslint-disable-next-line */ }, [baseUrl, userID]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setCarouselIndex(0);
  };
  const handleNext = (images) => setCarouselIndex((prev) => (prev + 1) % images.length);
  const handlePrev = (images) => setCarouselIndex((prev) => (prev - 1 + images.length) % images.length);

  // map server -> modal seed (normalized)
  const mapDetailsToModalSeed = (resp) => {
    const dishLbl = dishLabelFrom(resp);
    const iconUrl = firstIconUrl(resp);
    const custom = safeParse(resp?.customizations, null);

    return {
      itemId: String(resp?.item_id || ""),
      itemName: resp?.item_name || "",
      itemDescription: resp?.item_description || "",
      itemNameTrans: resp?.item_name || "",
      itemDescriptionTrans: resp?.item_description || "",
      category: resp?.category_name || resp?.category || "",
      subCategory: resp?.sub_category || "",
      categoryDescription: "[]",
      position: resp?.position ? String(resp.position) : "",
      dish: dishLbl,                 // human label
      iconDish: iconUrl || "",       // first icon (if any)
      photo: resp?.photo || "",
      ratings: resp?.ratings ? String(resp.ratings) : "",
      price: resp?.price ? String(resp.price) : "",
      discount: resp?.discount ? String(resp.discount) : "",
      discountPrice: resp?.discount_price ? String(resp.discount_price) : "",
      size: resp?.size || "",
      sizeId: resp?.size_id ? String(resp.size_id) : "",
      sizeTrans: resp?.size || "",
      catId: resp?.cat_id ? String(resp.cat_id) : "",
      status: (resp?.inStock === "Y" || resp?.status === "Y" || resp?.inStock === true) ? "Y" : "N",
      customizations: Array.isArray(custom) ? JSON.stringify(custom) : (resp?.customizations || "[]"),
      barcodeNumber: resp?.barcodeNumber || resp?.barcode || "",
    };
  };

  const fetchItemDetails = async (itemId, openModal = false) => {
    if (!itemId) return;
    try {
      setIsSearching(true);
      const res = await fetch(`${baseUrl}/api/Inventory/v1.0/getInventoryItemDetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${BASIC_AUTH}`,
        },
        body: JSON.stringify({ userid: userID, itemId: String(itemId) }),
      });
      const data = await res.json();
      if (data?.responseCode === "00" && data?.response) {
        const seed = mapDetailsToModalSeed(data.response);
        setEditSeed(seed);
        if (openModal) setShowEditModal(true);
        Swal.fire({
          icon: "success",
          title: "Item loaded",
          text: `Ready to update ${data.response.item_name || ""}`,
          timer: 900,
          showConfirmButton: false,
        });
      } else {
        setEditSeed(null);
        Swal.fire("Not Found", "No item found for this ID", "warning");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch item details", "error");
    } finally {
      setIsSearching(false);
    }
  };

  const onBarcodeKeyDown = (e) => {
    if (e.key === "Enter") {
      const code = barcodeValue.trim();
      if (!code) return;
      fetchItemDetails(code, true);
    }
  };

  // categories / subcategories
  const fetchInventoryCategories = async () => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getInventoryCategories`,
        { userid: userID },
        config
      );
      if (res.data?.responseCode === "00") {
        const list = (res.data.response || []).map((r) => ({
          label: r.category,
          value: r.category,
          catId: r.catId || "",
        }));
        setInvCategories(list);
        return list;
      }
    } catch (err) {
      console.error("getInventoryCategories error:", err);
    }
    setInvCategories([]);
    return [];
  };

  const fetchInventorySubCategories = async (category) => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/Inventory/v1.0/getInventorySubCategories`,
        { userid: userID, category },
        config
      );
      if (res.data?.responseCode === "00") {
        const list = (res.data.response || []).map((r) => ({
          label: r.subCategory || r.sub_category || r.category,
          value: r.subCategory || r.sub_category || r.category,
        }));
        setInvSubCategories(list);
        return list;
      }
    } catch (err) {
      console.error("getInventorySubCategories error:", err);
    }
    setInvSubCategories([]);
    return [];
  };

  useEffect(() => {
    (async () => {
      const cats = await fetchInventoryCategories();
      if (cats.length) {
        await fetchInventorySubCategories(cats[0].value);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, userID]);
  // --- Clean item mapper: prefers `id`, still keeps `item_id` as code ---
const mapInventoryItem = (it) => {
  const dishLabel = dishLabelFrom(it);
  const discount = it?.discount ? String(it.discount) : "";
  const net = it?.discount_price ? String(it.discount_price) : "";
  const id =
    String(
      it?.id ??
      it?.pk_item_id ??
      it?.itemId ??
      it?.item_id ??            // fallback if API doesn't send `id`
      ""
    );

  const code = String(it?.item_id ?? id); // show this in “Code” column
  const barcodeNumber = it?.barcodeNumber || it?.barcode || "";

  return {
    // ✅ expand & key by THIS `id`
    id,

    // visible table fields
    code,
    name: it?.item_name || "",
    group: it?.category_name || "Unknown",
    category: it?.sub_category || it?.item_category || "-",
    price: `₹${it?.price ?? "0"}`,
    lastUpdated: it?.lastUpdated || "—",
    inStock: it?.inStock === "Y" || it?.status === "Y" || it?.inStock === true,
    image: it?.photo || "",
    expandedImages: [it?.photo].filter(Boolean),

    // extra
    barcodeNumber,

    // expanded details grid (readable keys only)
    details: {
      "Item Name": it?.item_name || "",
      "Category": it?.category_name || "",
      "Sub Category": it?.sub_category || "",
      "Size": it?.size || "",
      "Dish": dishLabel || "—",
      "Price (MRP)": `₹${it?.price ?? "0"}`,
      "Discount %": discount || "0",
      "Discounted Price": net ? `₹${net}` : "₹0",
      "Ratings": it?.ratings ?? "0",
      "Status": (it?.inStock === "Y" || it?.status === "Y") ? "Active (Y)" : "Inactive (N)",
      "Stock": it?.stock ?? "0",
      "Home Delivery": it?.home_delivery === "Y" ? "Yes" : "No",
      "Takeaway": it?.takeaway === "Y" ? "Yes" : "No",
      "Dining": it?.dining === "Y" ? "Yes" : "No",
      "Item Type": it?.item_category || "",
      "ID": id,                  // ← true primary id
      "Code (item_id)": code,    // ← legacy code
      ...(barcodeNumber ? { "Barcode": barcodeNumber } : {}),
    },

    _raw: it, // keep original if you need more fields later
  };
};


  return (
    <div className="inv-accordion-wrapper">
      {/* Header */}
      <div className="inv-header">
        <h2 className="CreateCampaign">Inventory Management</h2>
        <div className="inv-controls">
          <select className="inv-badge-select colorful"><option>Category</option></select>
          <select className="inv-badge-select colorful"><option>Brand</option></select>

          {/* Barcode / ItemID search */}
          <div className={`inv-search ${isSearching ? "is-loading" : ""}`}>
            <Search size={16} />
            <input
              placeholder="Scan / enter Barcode or Item ID"
              autoFocus
              value={barcodeValue}
              onChange={(e) => setBarcodeValue(e.target.value)}
              onKeyDown={onBarcodeKeyDown}
            />
            {isSearching && <RefreshCcw className="spin ms-2" size={16} />}
          </div>

          {/* Quick search */}
          <div className="inv-search">
            <Search size={16} />
            <input
              placeholder="Quick Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button className="vendor-btn-add" onClick={() => setShowAddModal(true)}>
            <Plus size={16} /> Add Product
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Loader />
      ) : (
        <div className="inv-table">
          <div className="inv-main-row inv-heading">
            <span></span>
            <span>Image</span>
            <span>Name</span>
            <span>Code</span>
            <span>Group</span>
            <span>Category</span>
            <span>Price</span>
            <span>Last Updated</span>
            <span></span>
          </div>

          {inventoryData
            .filter((it) => {
              if (!q.trim()) return true;
              const needle = q.toLowerCase();
              return (
                it.name.toLowerCase().includes(needle) ||
                it.code.toLowerCase().includes(needle) ||
                it.group.toLowerCase().includes(needle) ||
                it.category.toLowerCase().includes(needle)
              );
            })
            .map((item) => (
              <div className="inv-row" key={item.id}>
                <div className="inv-main-row">
                  <input type="checkbox" defaultChecked={item.inStock} aria-label="in-stock" />
                  <img src={item.image} alt={item.name} className="inv-thumbnail" />
                  <span className="inv-item-name">{item.name}</span>
                  <span className="inv-item-code">{item.code}</span>
                  <span className="inv-badge colorful">{(item.group || "-").slice(0, 16)}</span>
                  <span className="inv-badge colorful">{(item.category || "-").slice(0, 16)}</span>
                  <span>{item.price}</span>
                  <span>{item.lastUpdated}</span>
                  <button className="inv-expand-btn" onClick={() => toggleExpand(item.id)}>
                    {expandedId === item.id ? <ChevronUp /> : <ChevronDown />}
                  </button>
                </div>

                {expandedId === item.id && (
                  <div className="inv-expanded-panel">
                    <div className="inv-left">
                      <div className="inv-carousel-wrapper">
                        <button className="inv-carousel-nav left" onClick={() => handlePrev(item.expandedImages)}>&lt;</button>
                        {item.expandedImages.length > 0 ? (
                          <img
                            src={item.expandedImages[carouselIndex]}
                            alt="carousel"
                            className="inv-carousel-img-single"
                          />
                        ) : (
                          <div className="inv-carousel-img-single inv-img-ph">No Image</div>
                        )}
                        <button className="inv-carousel-nav right" onClick={() => handleNext(item.expandedImages)}>&gt;</button>
                      </div>

                      <button
                        className="btn-light border-none rounded  mt-1"
                        onClick={() => {
                          fetchItemDetails(item.id, true); // authoritative fetch
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        title="Edit this item"
                      >
                        <Pencil size={16} /> Edit
                      </button>
                    </div>

                    <div className="inv-right">
                      <div className="inv-grid">
                        {Object.entries(item.details).map(([key, value]) => (
                          <div key={key} className="inv-grid-item">
                            <strong>{key}:</strong>
                            {Array.isArray(value) ? (
                              <div className="inv-tags">
                                {value.map((tag, i) => (
                                  <span key={i} className="inv-tag">{tag}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="inv-value">{String(value)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}

      {/* ADD */}
      <CreateOrderModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        userID={userID}
        BASIC_AUTH={BASIC_AUTH}
        baseUrl={baseUrl}
        categories={invCategories}
        subcategories={invSubCategories}
        initialCategory={invCategories[0]?.value || ""}
        loadSubcategories={fetchInventorySubCategories}
        mode="add"
        onSaved={() => {
          setShowAddModal(false);
          fetchInventory();
        }}
      />

      {/* EDIT */}
      <CreateOrderModal
        show={showEditModal}
        onClose={() => { setShowEditModal(false); setEditSeed(null); }}
        userID={userID}
        BASIC_AUTH={BASIC_AUTH}
        baseUrl={baseUrl}
        categories={invCategories}
        subcategories={invSubCategories}
        initialCategory={invCategories[0]?.value || ""}
        loadSubcategories={fetchInventorySubCategories}
        mode="edit"
        seedFrom={editSeed}
        onSaved={() => {
          setShowEditModal(false);
          setEditSeed(null);
          fetchInventory();
        }}
      />
    </div>
  );
};

export default InventoryAccordion;
